import {
  FC,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { Form, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Option } from '@beeline/design-system-react/extracted-styles';

import { getSharings } from 'src/api/places';
import { readCarInfo } from 'src/api/plan';
import { Town } from 'src/api/types';
import { messagesActions } from 'src/store/messages/slice';
import { getParkingTown } from 'src/store/parking/selectors';
import {
  bookParking,
  shareParkingPlace,
  unbookParking,
} from 'src/store/parking/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Parking } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';

import { ReservedBySpaceInfo } from '../../ReservedBySpaceInfo';
import { AdminBookingFields } from '../../panels/AdminBooking';
import { BusyBookingFields } from '../../panels/Busy';
import { GuestInfo } from '../../panels/GuestInfo';
import { RegularBookingFields } from '../../panels/RegularBooking';
import {
  Mode,
  RoomBookingPageModeSwitcher,
} from '../../panels/ReservedByEmployee/RoomBookingPageModeSwitcher';
import { ReservedByEmployeeInfo } from '../../panels/ReservedByEmployeeInfo';
import { ShareFields } from '../../panels/Share';
import { BottomSheetWrapper } from '../BottomSheetWrapper/BottomSheetWrapper';

import classes from './ParkingBottomSheet.module.scss';

const getCurrentDate = (town: Town | undefined, date: Date): string => {
  const currentDate = getDateTime({
    date,
    timezoneOffset: town?.timeZone,
  });
  return `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate()}`;
};

export interface ParkingSheetFormValues {
  dates: Set<string>;
  guest: boolean;
  model: string;
  number: string;
  needPass: boolean;
  responsible: Option<string> | null;
  guestName: string;
}

const defaultInitialValues: ParkingSheetFormValues = {
  dates: new Set<string>(),
  guest: false,
  model: '',
  number: '',
  needPass: false,
  responsible: null,
  guestName: '',
};

interface ShareProps {
  parking: Parking;
}

const Share: FC<ShareProps> = ({ parking }) => {
  const dispatch = useAppDispatch();

  const town = useSelector(getParkingTown);
  const { change } = useForm();

  const init = useCallback(async () => {
    try {
      const sharings = await getSharings(parking.id!);

      change(
        'dates',
        new Set(
          sharings.map(sharing =>
            getCurrentDate(town, new Date(sharing.sharedFrom)),
          ),
        ),
      );
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    }
  }, [change, dispatch, parking.id, town]);

  useEffect(() => {
    init();
  }, [init]);

  return <ShareFields responsible={parking.responsibleEmployeeNumber!} />;
};

interface RegularProps {
  parking: Parking;
  employeeNumber: string;
}

const Regular: FC<RegularProps> = ({ parking, employeeNumber }) => {
  const dispatch = useAppDispatch();

  const { change } = useForm();

  const init = useCallback(async () => {
    try {
      const carInfo = await readCarInfo(employeeNumber);
      change('model', carInfo.model ?? '');
      change('number', carInfo.number ?? '');
      change('needPass', false);
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    }
  }, [change, dispatch, employeeNumber]);

  useEffect(() => {
    init();
  }, [init]);

  return <RegularBookingFields guest={parking.guest} status={parking.status} />;
};

export interface ParkingBottomSheetProps {
  parking?: Parking;
  employeeNumber: string;
  adminSpaces: Set<number>;
  spaces: Set<number>;
}

export const ParkingBottomSheet: FC<ParkingBottomSheetProps> = ({
  parking,
  employeeNumber,
  adminSpaces,
  spaces,
}) => {
  const town = useSelector(getParkingTown);

  const dispatch = useAppDispatch();

  const [mode, setMode] = useState<Mode>(Mode.Sharing);
  const isAdmin = !!adminSpaces.size;
  const isAdminOfSpace =
    isAdmin && (!parking?.spaceId || adminSpaces.has(parking.spaceId));
  const isReservedByEmployee = !!parking?.responsibleEmployeeNumber;
  const isReservedBySpace = !!parking?.spaceId && !spaces.has(parking.spaceId);
  const isShared = parking?.shared;
  const isGuest = !isAdmin && parking?.guest;
  const hasPermissionToUnbook =
    parking?.status &&
    (parking.status.authorEmployeeNumber === employeeNumber ||
      parking.status.responsibleEmployeeNumber === employeeNumber ||
      isAdminOfSpace);
  const bookingIsEnabled = !!parking?.status || !!parking?.shared;

  const [initialValues, setInitialValues] =
    useState<ParkingSheetFormValues>(defaultInitialValues);

  const init = useCallback(async () => {
    try {
      if (!parking?.id) return;

      const sharings = await getSharings(parking.id);
      setInitialValues({
        ...defaultInitialValues,
        dates: new Set(
          sharings.map(sharing =>
            getCurrentDate(town, new Date(sharing.sharedFrom)),
          ),
        ),
      });
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    }
  }, [dispatch, parking?.id, town]);

  useEffect(() => {
    init();
  }, [init]);

  const share = useCallback(
    async (values: ParkingSheetFormValues) => {
      const result = await dispatch(
        shareParkingPlace([...values.dates]),
      ).unwrap();
      if (!result) init();

      return result;
    },
    [dispatch, init],
  );

  const book = useCallback(
    async (values: ParkingSheetFormValues) => {
      const responsible = isAdmin
        ? values.guest
          ? undefined
          : (values.responsible?.id as string)
        : employeeNumber;

      return dispatch(
        bookParking({
          responsible,
          guestName: values.guest ? values.guestName : undefined,
          carModel: values.model,
          carNumber: values.number,
          needPass: values.guest ? true : values.needPass,
        }),
      ).unwrap();
    },
    [dispatch, employeeNumber, isAdmin],
  );

  const unbook = useCallback(async () => {
    return dispatch(unbookParking()).unwrap();
  }, [dispatch]);

  const [fields, action, handleClick] = useMemo((): [
    ReactNode,
    string | undefined,
    ((values: ParkingSheetFormValues) => Promise<any>) | undefined,
  ] => {
    if (!parking) return [null, undefined, undefined];

    if (isAdmin && isReservedByEmployee) {
      let button: string | undefined = undefined;
      let fn: ((values: ParkingSheetFormValues) => Promise<any>) | undefined =
        undefined;

      if (mode === Mode.Sharing) {
        button = 'Сохранить';
        fn = share;
      } else {
        if (parking.status) {
          if (hasPermissionToUnbook) {
            button = 'Отменить бронь';
            fn = unbook;
          }
        } else {
          if (bookingIsEnabled) {
            button = 'Забронировать';
            fn = book;
          }
        }
      }

      return [
        <>
          {mode === Mode.Sharing && (
            <Share parking={parking} key={parking.mapPlaceId} />
          )}
          {mode === Mode.Booking && (
            <>
              {parking.status ? (
                <BusyBookingFields
                  status={parking.status}
                  key={parking.mapPlaceId}
                />
              ) : (
                <AdminBookingFields
                  parking={parking}
                  key={parking.mapPlaceId}
                />
              )}
            </>
          )}
        </>,
        button,
        fn,
      ];
    }

    if (parking.status) {
      return [
        // eslint-disable-next-line react/jsx-key
        <BusyBookingFields status={parking.status} key={parking.mapPlaceId} />,
        hasPermissionToUnbook ? 'Отменить бронь' : undefined,
        hasPermissionToUnbook ? unbook : undefined,
      ];
    }

    if (isAdmin) {
      // eslint-disable-next-line react/jsx-key
      return [
        <AdminBookingFields parking={parking} key={parking.mapPlaceId} />,
        'Забронировать',
        book,
      ];
    }

    if (isReservedByEmployee && !isShared) {
      return [
        // eslint-disable-next-line react/jsx-key
        <ReservedByEmployeeInfo
          responsible={parking.responsibleEmployeeNumber!}
          key={parking.mapPlaceId}
        />,
        undefined,
        undefined,
      ];
    }

    if (isReservedBySpace) {
      return [
        // eslint-disable-next-line react/jsx-key
        <ReservedBySpaceInfo
          spaceId={parking.spaceId!}
          spaceAdministrator={parking.spaceAdministrator}
          key={parking.mapPlaceId}
        />,
        undefined,
        undefined,
      ];
    }

    if (isGuest) {
      return [
        // eslint-disable-next-line react/jsx-key
        <GuestInfo
          guest
          spaceAdministrator={parking.spaceAdministrator}
          key={parking.mapPlaceId}
        />,
        undefined,
        undefined,
      ];
    }

    return [
      // eslint-disable-next-line react/jsx-key
      <Regular
        parking={parking}
        employeeNumber={employeeNumber}
        key={parking.mapPlaceId}
      />,
      'Забронировать',
      book,
    ];
  }, [
    book,
    bookingIsEnabled,
    employeeNumber,
    hasPermissionToUnbook,
    isAdmin,
    isGuest,
    isReservedByEmployee,
    isReservedBySpace,
    isShared,
    mode,
    parking,
    share,
    unbook,
  ]);

  const submit = useCallback(
    async (values: ParkingSheetFormValues) => {
      return handleClick?.(values);
    },
    [handleClick],
  );

  return (
    <Form initialValues={initialValues} onSubmit={submit}>
      {() => (
        <BottomSheetWrapper
          title={parking?.title}
          action={action}
          containerClassName={classes.container}
          bodyClassName={classes.body}
          header={
            isAdmin && isReservedByEmployee ? (
              <RoomBookingPageModeSwitcher
                mode={mode}
                setMode={setMode}
                bookingIsEnabled={bookingIsEnabled}
              />
            ) : null
          }
        >
          {fields}
        </BottomSheetWrapper>
      )}
    </Form>
  );
};
